import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { Property } from '../../types/property';

interface ShowcasePDFProps {
  property: Property;
  agencyName: string;
  agencyLogo?: string;
  agencyContact: string;
  brandColor?: string;
  orientation?: 'portrait' | 'landscape';
}

export const ShowcasePDF: React.FC<ShowcasePDFProps> = ({
  property,
  agencyName,
  agencyLogo,
  agencyContact,
  brandColor = '#0F766E',
  orientation = 'portrait',
}) => {
  console.log('📄 ShowcasePDF - Props reçues:', {
    agencyName,
    agencyLogo,
    agencyContact,
    brandColor,
    orientation
  });

  const isLandscape = orientation === 'landscape';
  const equipmentsTopPosition = 600;

  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      flexDirection: 'column',
      height: '100%',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: isLandscape ? 25 : 30,
      paddingVertical: isLandscape ? 15 : 18,
      borderBottom: 2,
      borderBottomColor: '#E5E7EB',
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isLandscape ? 8 : 12,
    },
    headerRight: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: isLandscape ? 3 : 4,
    },
    logo: {
      width: isLandscape ? 80 : 100,
      height: isLandscape ? 40 : 50,
      objectFit: 'contain',
    },
    agencyNameText: {
      fontSize: isLandscape ? 11 : 13,
      fontWeight: 'bold',
      color: '#1F2937',
      letterSpacing: 0.5,
    },
    contactText: {
      fontSize: isLandscape ? 10 : 11,
      color: '#374151',
      fontWeight: '600',
    },
    contactItem: {
      fontSize: isLandscape ? 9.5 : 10.5,
      color: '#1F2937',
      fontWeight: '500',
    },
    mainContainer: {
      flex: 1,
      padding: isLandscape ? 20 : 25,
      flexDirection: isLandscape ? 'column' : 'column',
      gap: isLandscape ? 20 : 0,
    },
    photoSection: {
      width: isLandscape ? '56%' : '100%',
      height: isLandscape ? 254 : 240,
      marginBottom: isLandscape ? 0 : 18,
      alignSelf: isLandscape ? 'flex-end' : 'stretch',
      borderRadius: 8,
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    mainImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      backgroundColor: '#F3F4F6',
      justifyContent: 'center',
      alignItems: 'center',
    },
    placeholderText: {
      fontSize: isLandscape ? 10 : 12,
      color: '#9CA3AF',
    },
    statusBadge: {
      position: 'absolute',
      top: isLandscape ? 10 : 15,
      left: isLandscape ? 10 : 15,
      backgroundColor: '#FFFFFF',
      paddingHorizontal: isLandscape ? 12 : 16,
      paddingVertical: isLandscape ? 6 : 8,
      borderRadius: isLandscape ? 20 : 30,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    statusText: {
      fontSize: isLandscape ? 8 : 10,
      fontWeight: 'bold',
      color: brandColor,
      letterSpacing: isLandscape ? 0.5 : 1,
    },
    infoGrid: {
      flexDirection: isLandscape ? 'row' : 'row',
      gap: isLandscape ? 15 : 20,
      flex: isLandscape ? 1 : 1,
      position: isLandscape ? 'absolute' : 'relative',
      top: isLandscape ? 40 : 'auto',
      left: isLandscape ? 20 : 'auto',
      right: isLandscape ? '46%' : 'auto',
    },
    leftColumn: {
      flex: 1,
    },
    propertyTitleSection: {
      marginBottom: isLandscape ? 12 : 15,
    },
    propertyType: {
      fontSize: isLandscape ? 16 : 20,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: isLandscape ? 4 : 6,
      letterSpacing: -0.3,
    },
    propertyAddress: {
      fontSize: isLandscape ? 8 : 10,
      color: '#6B7280',
      lineHeight: 1.4,
    },
    featuresGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: isLandscape ? 6 : 8,
    },
    featureCard: {
      width: isLandscape ? '48%' : '48%',
      backgroundColor: '#F9FAFB',
      padding: isLandscape ? 8 : 10,
      borderRadius: isLandscape ? 4 : 6,
      borderLeft: 3,
      borderLeftColor: brandColor,
    },
    featureLabel: {
      fontSize: isLandscape ? 7 : 8,
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: isLandscape ? 2 : 3,
    },
    featureValue: {
      fontSize: isLandscape ? 11 : 13,
      fontWeight: 'bold',
      color: '#111827',
    },
    rightColumn: {
      flex: 1,
      flexDirection: 'column',
    },
    sectionTitle: {
      fontSize: isLandscape ? 9 : 11,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: isLandscape ? 6 : 10,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    descriptionSection: {
      marginBottom: isLandscape ? 10 : 12,
      alignItems: isLandscape ? 'flex-start' : 'flex-start',
      flex: isLandscape ? 0 : 1,
      position: isLandscape ? 'absolute' : 'relative',
      top: isLandscape ? 280 : 'auto',
      left: isLandscape ? 20 : 'auto',
      right: isLandscape ? 20 : 'auto',
      width: isLandscape ? 'auto' : 'auto',
    },
    descriptionText: {
      fontSize: isLandscape ? 11 : 10,
      color: '#4B5563',
      lineHeight: isLandscape ? 1.5 : 1.6,
      textAlign: isLandscape ? 'justify' : 'justify',
    },
    equipmentsSection: {
      marginBottom: isLandscape ? 10 : 10,
      alignItems: isLandscape ? 'center' : 'flex-start',
      position: isLandscape ? 'absolute' : 'absolute',
      top: isLandscape ? 390 : equipmentsTopPosition,
      left: isLandscape ? 0 : 25,
      right: isLandscape ? 0 : 25,
    },
    equipmentsList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: isLandscape ? 4 : 6,
      justifyContent: isLandscape ? 'center' : 'flex-start',
      width: '100%',
    },
    equipmentBadge: {
      backgroundColor: `${brandColor}15`,
      paddingHorizontal: isLandscape ? 8 : 10,
      paddingVertical: isLandscape ? 4 : 6,
      borderRadius: isLandscape ? 15 : 20,
      border: 1,
      borderColor: `${brandColor}40`,
      marginRight: 5,
      marginBottom: 5,
    },
    equipmentText: {
      fontSize: isLandscape ? 12 : 10,
      color: brandColor,
      fontWeight: '500',
    },
    priceBadgeContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: isLandscape ? 10 : 12,
      position: 'absolute',
      bottom: isLandscape ? 30 : 30, // Descendu de 30 pixels en portrait (50 → 80)
      left: 0,
      right: 0,
    },
    priceBadge: {
      backgroundColor: `${brandColor}20`,
      paddingHorizontal: isLandscape ? 30 : 35,
      paddingVertical: isLandscape ? 14 : 16,
      borderRadius: isLandscape ? 45 : 50,
      border: 2,
      borderColor: brandColor,
      alignItems: 'center',
    },
    priceAmount: {
      fontSize: isLandscape ? 28 : 32,
      fontWeight: 'bold',
      color: brandColor,
      letterSpacing: -0.5,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: isLandscape ? 25 : 30,
      paddingVertical: isLandscape ? 8 : 12,
      borderTop: 1,
      borderTopColor: '#E5E7EB',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    footerText: {
      fontSize: isLandscape ? 7 : 8,
      color: '#9CA3AF',
    },
    referenceText: {
      fontSize: isLandscape ? 7 : 8,
      color: '#6B7280',
      fontWeight: '500',
    },
  });

  const generateDescription = () => {
    const area = Math.floor(property.features?.area || property.area || 0);
    const rooms = property.features?.rooms || property.rooms || 0;
    const floor = property.features?.floor || property.floor;
    const propertyType = property.type || 
      (property.property_category && property.property_subtype 
        ? `${property.property_category}${property.property_subtype !== 'Simple' ? ' ' + property.property_subtype : ''}`
        : property.property_category) || 'bien';
    let desc = `Découvrez ce superbe ${propertyType.toLowerCase()} de ${area} m² offrant ${rooms} pièce${rooms > 1 ? 's' : ''}`;
    if (floor !== undefined && floor > 0) {
      desc += `, situé au ${floor}${floor === 1 ? 'er' : 'ème'} étage`;
    }
    desc += `. `;
    const features = [];
    if (property.features?.hasBalcony || property.has_balcony) features.push('balcon');
    if (property.features?.hasTerrace || property.has_terrace) features.push('terrasse');
    if (property.features?.hasParking || property.has_parking) features.push('parking');
    if (features.length > 0) {
      desc += `Ce bien bénéficie de ${features.join(', ')}. `;
    }
    desc += `Idéalement situé, ce bien allie confort et praticité.`;
    return desc;
  };

  const getEquipments = () => {
    const equip = [];
    if (property.features?.hasElevator || property.has_elevator) equip.push('Ascenseur');
    if (property.features?.hasParking || property.has_parking) equip.push('Parking');
    if (property.features?.hasBalcony || property.has_balcony) equip.push('Balcon');
    if (property.features?.hasTerrace || property.has_terrace) equip.push('Terrasse');
    if (property.features?.hasAirConditioning || property.has_air_conditioning) equip.push('Climatisation');
    if (property.features?.heatingType || property.heating_type) {
      equip.push(`Chauffage ${property.features?.heatingType || property.heating_type}`);
    }
    return equip;
  };

  const getStatusLabel = () => {
    const labels: Record<string, string> = {
      'for-sale': 'À VENDRE',
      'for-rent': 'À LOUER',
      'for-short-rent': 'LOCATION SAISONNIÈRE',
      estimation: 'ESTIMATION',
    };
    return labels[property.status] || 'DISPONIBLE';
  };

  const formatPrice = () => {
    const price = Math.floor(property.price || 0);
    return price.toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  // Fonction pour obtenir le type de bien de manière robuste
  const getPropertyType = () => {
    // Priorité à property_category + property_subtype
    if (property.property_category) {
      return property.property_subtype && property.property_subtype !== 'Simple'
        ? `${property.property_category} ${property.property_subtype}`
        : property.property_category;
    }
    // Fallback sur property.type
    return property.type || 'Bien immobilier';
  };

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {agencyLogo ? (
              <Image style={styles.logo} src={agencyLogo} />
            ) : (
              <Text style={styles.agencyNameText}>{agencyName}</Text>
            )}
          </View>
          <View style={styles.headerRight}>
            {agencyContact.split('•').map((contact, index) => (
              <Text key={index} style={styles.contactItem}>
                {contact.trim()}
              </Text>
            ))}
          </View>
        </View>

        {/* Contenu principal */}
        <View style={styles.mainContainer}>
          {isLandscape ? (
            <>
              {/* Version PAYSAGE */}
              <View style={styles.infoGrid}>
                <View style={styles.leftColumn}>
                  <View style={styles.propertyTitleSection}>
                    <Text style={styles.propertyType}>
                      {getPropertyType()}
                    </Text>
                    <Text style={styles.propertyAddress}>{property.address || 'Adresse non spécifiée'}</Text>
                  </View>
                  <View style={styles.featuresGrid}>
                    <View style={styles.featureCard}>
                      <Text style={styles.featureLabel}>Surface</Text>
                      <Text style={styles.featureValue}>
                        {Math.floor(property.features?.area || property.area || 0)} m²
                      </Text>
                    </View>
                    <View style={styles.featureCard}>
                      <Text style={styles.featureLabel}>Pièces</Text>
                      <Text style={styles.featureValue}>{property.features?.rooms || property.rooms || 0}</Text>
                    </View>
                    {(property.features?.floor !== undefined || property.floor !== undefined) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Étage</Text>
                        <Text style={styles.featureValue}>{property.features?.floor ?? property.floor}</Text>
                      </View>
                    )}
                    {(property.features?.constructionYear || property.construction_year) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Année</Text>
                        <Text style={styles.featureValue}>
                          {property.features?.constructionYear || property.construction_year}
                        </Text>
                      </View>
                    )}
                    {(property.features?.bedrooms || property.bedrooms) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Chambres</Text>
                        <Text style={styles.featureValue}>{property.features?.bedrooms || property.bedrooms}</Text>
                      </View>
                    )}
                    {(property.features?.bathrooms || property.bathrooms) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Salles de bain</Text>
                        <Text style={styles.featureValue}>{property.features?.bathrooms || property.bathrooms}</Text>
                      </View>
                    )}
                    {(property.features?.hasParking || property.has_parking) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Parking</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                    {(property.features?.hasElevator || property.has_elevator) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Ascenseur</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                    {property.features?.orientation && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Orientation</Text>
                        <Text style={styles.featureValue}>{property.features.orientation}</Text>
                      </View>
                    )}
                    {(property.features?.hasBalcony || property.has_balcony) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Balcon</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
              <View style={styles.rightColumn}>
                <View style={styles.photoSection}>
                  {property.photos && property.photos[0] ? (
                    <Image style={styles.mainImage} src={property.photos[0]} />
                  ) : (
                    <View style={styles.imagePlaceholder}>
                      <Text style={styles.placeholderText}>Photo non disponible</Text>
                    </View>
                  )}
                  <View style={styles.statusBadge}>
                    <Text style={styles.statusText}>{getStatusLabel()}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.descriptionSection}>
                <Text style={styles.descriptionText}>{property.description || generateDescription()}</Text>
              </View>
              {/* Équipements en paysage */}
              {getEquipments().length > 0 && (
                <View style={styles.equipmentsSection}>
                  <View style={styles.equipmentsList}>
                    {getEquipments().map((equipment, index) => (
                      <View key={index} style={styles.equipmentBadge}>
                        <Text style={styles.equipmentText}>{equipment}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </>
          ) : (
            <>
              {/* Version PORTRAIT */}
              <View style={styles.photoSection}>
                {property.photos && property.photos[0] ? (
                  <Image style={styles.mainImage} src={property.photos[0]} />
                ) : (
                  <View style={styles.imagePlaceholder}>
                    <Text style={styles.placeholderText}>Photo non disponible</Text>
                  </View>
                )}
                <View style={styles.statusBadge}>
                  <Text style={styles.statusText}>{getStatusLabel()}</Text>
                </View>
              </View>
              <View style={styles.infoGrid}>
                <View style={styles.leftColumn}>
                  <View style={styles.propertyTitleSection}>
                    <Text style={styles.propertyType}>
                      {getPropertyType()}
                    </Text>
                    <Text style={styles.propertyAddress}>{property.address || 'Adresse non spécifiée'}</Text>
                  </View>
                  <View style={styles.featuresGrid}>
                    <View style={styles.featureCard}>
                      <Text style={styles.featureLabel}>Surface</Text>
                      <Text style={styles.featureValue}>
                        {Math.floor(property.features?.area || property.area || 0)} m²
                      </Text>
                    </View>
                    <View style={styles.featureCard}>
                      <Text style={styles.featureLabel}>Pièces</Text>
                      <Text style={styles.featureValue}>{property.features?.rooms || property.rooms || 0}</Text>
                    </View>
                    {(property.features?.floor !== undefined || property.floor !== undefined) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Étage</Text>
                        <Text style={styles.featureValue}>{property.features?.floor ?? property.floor}</Text>
                      </View>
                    )}
                    {(property.features?.constructionYear || property.construction_year) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Année</Text>
                        <Text style={styles.featureValue}>
                          {property.features?.constructionYear || property.construction_year}
                        </Text>
                      </View>
                    )}
                    {(property.features?.bedrooms || property.bedrooms) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Chambres</Text>
                        <Text style={styles.featureValue}>{property.features?.bedrooms || property.bedrooms}</Text>
                      </View>
                    )}
                    {(property.features?.bathrooms || property.bathrooms) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Salles de bain</Text>
                        <Text style={styles.featureValue}>{property.features?.bathrooms || property.bathrooms}</Text>
                      </View>
                    )}
                    {(property.features?.hasParking || property.has_parking) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Parking</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                    {(property.features?.hasElevator || property.has_elevator) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Ascenseur</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                    {property.features?.orientation && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Orientation</Text>
                        <Text style={styles.featureValue}>{property.features.orientation}</Text>
                      </View>
                    )}
                    {(property.features?.hasBalcony || property.has_balcony) && (
                      <View style={styles.featureCard}>
                        <Text style={styles.featureLabel}>Balcon</Text>
                        <Text style={styles.featureValue}>Oui</Text>
                      </View>
                    )}
                  </View>
                </View>
                <View style={styles.rightColumn}>
                  <View style={styles.descriptionSection}>
                    <Text style={styles.sectionTitle}>Description</Text>
                    <Text style={styles.descriptionText}>{property.description || generateDescription()}</Text>
                  </View>
                </View>
              </View>
              {/* Équipements en portrait */}
              {getEquipments().length > 0 && (
                <View style={styles.equipmentsSection}>
                  <Text style={styles.sectionTitle}>Équipements</Text>
                  <View style={styles.equipmentsList}>
                    {getEquipments().map((equipment, index) => (
                      <View key={index} style={styles.equipmentBadge}>
                        <Text style={styles.equipmentText}>{equipment}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </>
          )}
        </View>

        {/* Prix en badge centré en bas */}
        <View style={styles.priceBadgeContainer}>
          <View style={styles.priceBadge}>
            <Text style={styles.priceAmount}>{formatPrice()}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.referenceText}>Référence : {property.id}</Text>
          <Text style={styles.footerText}>Document généré le {new Date().toLocaleDateString('fr-FR')}</Text>
        </View>
      </Page>
    </Document>
  );
};